import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, arrayRemove } from "firebase/firestore";
import { db } from "../config/Firebase";
import { useNavigate } from "react-router-dom";
import Header from "./../components/Header";
import Footer from "./../components/Footer";
import { FaTrashAlt } from "react-icons/fa";

// Import your brand images
import im6 from "../assets/images/im6.png";
import im7 from "../assets/images/im7.png";
import im8 from "../assets/images/im8.png";
import im9 from "../assets/images/im9.jpeg";
import im10 from "../assets/images/im10.jpeg";
import im11 from "../assets/images/im11.png";
import im12 from "../assets/images/im12.png";
import im13 from "../assets/images/im13.png";
import im14 from "../assets/images/im14.jpg";
import im15 from "../assets/images/im15.png";
import im16 from "../assets/images/im16.png";
import im17 from "../assets/images/im17.png";

export default function Order() {
  // Retrieve user details from local storage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const phone = user.phone;
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // List of collections where products might be stored
  const productCollections = [
    "DesignerMetal&MetalFrame",
    "FrameLess",
    "FullFrame",
    "HalfFrame",
    "SafetyGlassess",
    "Sunglassess",
    "Types",
    "UDB",
  ];

  // Optimized: For a given productId, try fetching concurrently from all collections
  const fetchProductById = async (productId) => {
    const queries = productCollections.map(async (collectionName) => {
      try {
        const productDocRef = doc(db, collectionName, productId);
        const productDocSnap = await getDoc(productDocRef);
        return productDocSnap.exists()
          ? {
              ...productDocSnap.data(),
              id: productDocSnap.id,
              collection: collectionName,
            }
          : null;
      } catch (err) {
        console.error(
          `Error fetching product ${productId} from ${collectionName}:`,
          err.message
        );
        return null;
      }
    });
    // Run all queries concurrently
    const results = await Promise.all(queries);
    // Return the first non-null result
    return results.find((result) => result !== null);
  };

  // Fetch wishlist products based on the user's phone number
  useEffect(() => {
    if (!phone) {
      setLoading(false);
      return;
    }

    const fetchWishlistProducts = async () => {
      try {
        const wishlistDocRef = doc(db, "Wishlist", phone);
        const wishlistDocSnap = await getDoc(wishlistDocRef);

        if (wishlistDocSnap.exists()) {
          const wishlistData = wishlistDocSnap.data();
          const productIds = wishlistData.ProductID || [];

          // For each product id, try to fetch the corresponding product document concurrently
          const productPromises = productIds.map((pid) => fetchProductById(pid));
          const fetchedProducts = await Promise.all(productPromises);

          // Filter out any null values (if a product was not found)
          setProducts(fetchedProducts.filter((p) => p !== null));
        } else {
          console.log("No wishlist document found for this user.");
        }
      } catch (err) {
        console.error("Error fetching wishlist products:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWishlistProducts();
  }, [phone]);

  // Define the brandImages array for the scrolling banner
  const brandImages = [
    im6,
    im7,
    im8,
    im9,
    im10,
    im11,
    im12,
    im13,
    im14,
    im15,
    im16,
    im17,
  ];

  const handleProductClick = (product) => {
    navigate(`/product-details`, { state: product });
  };

  // Function to delete a product from the wishlist
  const handleDeleteProduct = async (productId, e) => {
    // Prevent click event bubbling to the product card
    e.stopPropagation();
    try {
      const wishlistDocRef = doc(db, "Wishlist", phone);
      await updateDoc(wishlistDocRef, {
        ProductID: arrayRemove(productId),
      });
      // Remove product from local state so the UI updates immediately
      setProducts((prevProducts) =>
        prevProducts.filter((prod) => prod.ProductID !== productId)
      );
      console.log("Product removed from wishlist!");
    } catch (err) {
      console.error("Error removing product from wishlist:", err.message);
    }
  };

  return (
    <>
      <Header />

      {/* Scrolling banner */}
      <div className="scroll-container py-5 ">
        <div className="scroll-content">
          {brandImages.map((image, index) => (
            <img
              key={`brand-1-${index}`}
              src={image}
              alt="eyewear store"
              className="scroll-item"
            />
          ))}
          {brandImages.map((image, index) => (
            <img
              key={`brand-2-${index}`}
              src={image}
              alt="Eye checkup services"
              className="scroll-item"
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 py-10">
        <h1 className="text-2xl Rubik-Bold text-black mb-4 justify-center text-center">
          Wishlist
        </h1>

        {loading ? (
          <p className="text-center text-gray-600">Loading wishlist...</p>
        ) : error ? (
          <p className="text-center text-red-500">Error: {error}</p>
        ) : products.length === 0 ? (
          <p className="text-center text-gray-500">
            No products found in your wishlist.
          </p>
        ) : (
          <div className="space-y-4 flex flex-col items-center">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-gray-50 rounded-lg shadow-md p-4 flex items-center w-full sm:w-3/4 max-h-32 cursor-pointer"
                onClick={() => handleProductClick(product)}
              >
                {/* Product Image */}
                <div
                  className="flex-shrink-0 bg-gray-100 rounded-md"
                  style={{ width: "120px", height: "120px" }}
                >
                  {product.ImageUrl1 ? (
                    <img
                      src={product.ImageUrl1}
                      alt={product.Name || "Product Image"}
                      className="w-full h-full object-contain rounded-md"
                    />
                  ) : (
                    <p className="text-gray-400 italic text-center">
                      No image available
                    </p>
                  )}
                </div>

                {/* Product Details */}
                <div className="ml-4 justify-end text-sm space-y-1 w-full">
                  <div className="flex justify-between items-center">
                    <p className="Rubik-Bold text-gray-800">
                      {product.Name || "N/A"}
                    </p>
                    <div className="flex items-center space-x-3">
                      <p className="text-gray-600 Rubik-Light">
                        PID: {product.ProductID || "N/A"}
                      </p>
                      <button
                        onClick={(e) =>
                          handleDeleteProduct(product.ProductID, e)
                        }
                        className="text-red-500 px-5 text-xl"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                  <p className="text-black Rubik-Light">
                    <span className="Rubik-Bold">Frame Color:</span>{" "}
                    {product.FrameColor || "Unknown"}
                  </p>
                  <p className="text-black Rubik-Light">
                    <span className="Rubik-Bold">Material:</span>{" "}
                    {product.Material || "Unknown"}
                  </p>
                  <div className="relative flex items-center">
                    <span className="relative flex h-3 w-3 mr-2">
                      <span
                        className={`animate-ping absolute inline-flex h-full w-full rounded-full ${
                          product.Status === "Available"
                            ? "bg-sky-400"
                            : "bg-red-400"
                        } opacity-75`}
                      ></span>
                      <span
                        className={`relative inline-flex rounded-full h-3 w-3 ${
                          product.Status === "Available"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      ></span>
                    </span>
                    <span
                      className={`${
                        product.Status === "Available"
                          ? "text-green-500"
                          : "text-red-500"
                      } Rubik-Light`}
                    >
                      {product.Status || "Unknown"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </>
  );
}
